/**
 * Asynchronously loads the component for LoadingIndicator
 */
import LoadingIndicator from 'app/components/Loading';
import { lazyLoad } from 'utils/loadable';

const fallback = <LoadingIndicator />;

/**
 * App pages
 */
export const AppPage = lazyLoad(
  () => import(/* webpackChunkName: "AppPage" */ 'app/pages/AppPage'),
  module => module.default,
  { fallback, chunkName: 'AppPage' },
);

/**
 * Client pages
 */
export const ProjectClientPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "ProjectClientPage" */ 'app/pages/ClientPage/ProjectClientPage'),
  module => module.default,
  { fallback, chunkName: 'ProjectClientPage' },
);

export const ProjectClientAuthPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ProjectClientAuthPage" */ 'app/pages/ClientPage/ProjectClientAuthPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ProjectClientAuthPage' },
);

export const CancelRecurringPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "CancelRecurringPage" */ 'app/pages/ClientPage/CancelRecurringPage'
    ),
  module => module.default,
  { fallback, chunkName: 'CancelRecurringPage' },
);

export const CancelSuccessPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "CancelSuccessPage" */ 'app/pages/ClientPage/CancelRecurringPage/CancelSuccessPage'
    ),
  module => module.default,
  { fallback, chunkName: 'CancelSuccessPage' },
);

export const ChangeCardPage = lazyLoad(
  () => import(/* webpackChunkName: "ChangeCardPage" */ 'app/pages/ClientPage/ChangeCardPage'),
  module => module.default,
  { fallback, chunkName: 'ChangeCardPage' },
);

export const ChangeCardSuccessPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ChangeCardSuccessPage" */ 'app/pages/ClientPage/ChangeCardPage/ChangeCardSuccessPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ChangeCardSuccessPage' },
);

export const RePaymentPage = lazyLoad(
  () => import(/* webpackChunkName: "RePaymentPage" */ 'app/pages/ClientPage/RePaymentPage'),
  module => module.default,
  { fallback, chunkName: 'RePaymentPage' },
);

export const RePaymentSuccessPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "RePaymentSuccessPage" */ 'app/pages/ClientPage/RePaymentPage/RePaymentSuccessPage'
    ),
  module => module.default,
  { fallback, chunkName: 'RePaymentSuccessPage' },
);

export const PaymentPage = lazyLoad(
  () => import(/* webpackChunkName: "PaymentPage" */ 'app/pages/ClientPage/PaymentPage'),
  module => module.default,
  { fallback, chunkName: 'PaymentPage' },
);

export const EmbedPaymentPage = lazyLoad(
  () => import(/* webpackChunkName: "EmbedPaymentPage" */ 'app/pages/ClientPage/PaymentPage/Embed'),
  module => module.default,
  { fallback, chunkName: 'EmbedPaymentPage' },
);

export const PaymentPageStep1 = lazyLoad(
  () => import(/* webpackChunkName: "PaymentPageStep1" */ 'app/pages/ClientPage/PaymentPage/Step1'),
  module => module.default,
  { fallback, chunkName: 'PaymentPageStep1' },
);

export const PaymentPageStep2 = lazyLoad(
  () => import(/* webpackChunkName: "PaymentPageStep2" */ 'app/pages/ClientPage/PaymentPage/Step2'),
  module => module.default,
  { fallback, chunkName: 'PaymentPageStep2' },
);

export const PaymentPageStep3 = lazyLoad(
  () => import(/* webpackChunkName: "PaymentPageStep3" */ 'app/pages/ClientPage/PaymentPage/Step3'),
  module => module.default,
  { fallback, chunkName: 'PaymentPageStep3' },
);

// ======== START PAYMENT FORM V2 ===========
export const PaymentV2Page = lazyLoad(
  () => import(/* webpackChunkName: "PaymentV2Page" */ 'app/pages/ClientPage/PaymentV2Page'),
  module => module.default,
  { fallback, chunkName: 'PaymentPage' },
);

export const EmbedPaymentV2Page = lazyLoad(
  () =>
    import(/* webpackChunkName: "EmbedPaymentV2Page" */ 'app/pages/ClientPage/PaymentV2Page/Embed'),
  module => module.default,
  { fallback, chunkName: 'EmbedPaymentPage' },
);

export const PaymentV2PageStep1 = lazyLoad(
  () =>
    import(/* webpackChunkName: "PaymentV2PageStep1" */ 'app/pages/ClientPage/PaymentV2Page/Step1'),
  module => module.default,
  { fallback, chunkName: 'PaymentPageStep1' },
);

export const PaymentV2PageStep2 = lazyLoad(
  () =>
    import(/* webpackChunkName: "PaymentV2PageStep2" */ 'app/pages/ClientPage/PaymentV2Page/Step2'),
  module => module.default,
  { fallback, chunkName: 'PaymentPageStep2' },
);

export const PaymentV2PageStep3 = lazyLoad(
  () =>
    import(/* webpackChunkName: "PaymentV2PageStep3" */ 'app/pages/ClientPage/PaymentV2Page/Step3'),
  module => module.default,
  { fallback, chunkName: 'PaymentPageStep3' },
);
// ======== END PAYMENT FORM V2 ===========

export const PaymentSuccessPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "PaymentSuccessPage" */ 'app/pages/ClientPage/PaymentSuccessPage'),
  module => module.default,
  { fallback, chunkName: 'PaymentSuccessPage' },
);

export const BankPaymentSuccessPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "BankPaymentSuccessPage" */ 'app/pages/ClientPage/BankPaymentSuccessPage'
    ),
  module => module.default,
  { fallback, chunkName: 'BankPaymentSuccessPage' },
);

export const EntryPaymentSuccessPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "EntryPaymentSuccessPage" */ 'app/pages/ClientPage/EntryPaymentSuccessPage'
    ),
  module => module.default,
  { fallback, chunkName: 'EntryPaymentSuccessPage' },
);

export const IframeBoxPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IframeBoxPage" */ 'app/pages/ClientPage/ProjectClientPage/IframeBoxPage'
    ),
  module => module.default,
  { fallback, chunkName: 'IframeBoxPage' },
);

export const ScrollToTop = lazyLoad(
  () => import(/* webpackChunkName: "ScrollToTop" */ 'app/components/ScrollToTop'),
  module => module.default,
  { fallback, chunkName: 'ScrollToTop' },
);

/**
 * App pages
 */
export const HomePage = lazyLoad(
  () => import(/* webpackChunkName: "HomePage" */ 'app/pages/AppPage/HomePage'),
  module => module.default,
  { fallback, chunkName: 'HomePage' },
);

export const DummyPage = lazyLoad(
  () => import(/* webpackChunkName: "DummyPage" */ 'app/pages/AppPage/DummyPage'),
  module => module.default,
  { fallback, chunkName: 'DummyPage' },
);

export const LoginPage = lazyLoad(
  () => import(/* webpackChunkName: "LoginPage" */ 'app/pages/AppPage/LoginPage'),
  module => module.default,
  { fallback, chunkName: 'LoginPage' },
);

export const SupportLoginPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "SupportLoginPage" */ 'app/pages/AppPage/LoginPage/SupportLogin'),
  module => module.default,
  { fallback, chunkName: 'SupportLoginPage' },
);

export const RegisterThanksPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "RegisterThanksPage" */ 'app/pages/AppPage/LoginPage/RegisterThanks'
    ),
  module => module.default,
  { fallback, chunkName: 'RegisterThanksPage' },
);

export const RegisterOTPPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "RegisterThanksPage" */ 'app/pages/AppPage/LoginPage/RegisterOtp'),
  module => module.default,
  { fallback, chunkName: 'RegisterOTPPage' },
);

export const RegisterUserPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "RegisterUserPage" */ 'app/pages/AppPage/LoginPage/RegisterUser'),
  module => module.default,
  { fallback, chunkName: 'RegisterUserPage' },
);

export const RegisterCompletePage = lazyLoad(
  () =>
    import(/* webpackChunkName: "RegisterCompletePage" */ 'app/pages/AppPage/LoginPage/Complete'),
  module => module.default,
  { fallback, chunkName: 'RegisterCompletePage' },
);

export const ForgotPasswordPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ForgotPasswordPage" */ 'app/pages/AppPage/LoginPage/ForgotPassword'
    ),
  module => module.default,
  { fallback, chunkName: 'ForgotPasswordPage' },
);

export const ResetPasswordPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "ResetPasswordPage" */ 'app/pages/AppPage/LoginPage/ResetPassword'),
  module => module.default,
  { fallback, chunkName: 'ResetPasswordPage' },
);

export const RegisterReviewPage = lazyLoad(
  () => import(/* webpackChunkName: "ResetPasswordPage" */ 'app/pages/AppPage/VerificationPage'),
  module => module.default,
  { fallback, chunkName: 'ResetPasswordPage' },
);

export const RegisterStepsPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "RegisterStepsPage" */ 'app/pages/AppPage/VerificationPage/Steps'),
  module => module.default,
  { fallback, chunkName: 'RegisterStepsPage' },
);

export const VerificationConfirmationPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "VerificationConfirmationPage" */ 'app/pages/AppPage/VerificationPage/Confirmation'
    ),
  module => module.default,
  { fallback, chunkName: 'VerificationConfirmationPage' },
);

export const VerificationCompletePage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "VerificationCompletePage" */ 'app/pages/AppPage/VerificationPage/Complete'
    ),
  module => module.default,
  { fallback, chunkName: 'VerificationCompletePage' },
);

export const RegisterOtp = lazyLoad(
  () => import(/* webpackChunkName: "EmailVerifyPage" */ 'app/pages/AppPage/LoginPage/RegisterOtp'),
  module => module.default,
  { fallback, chunkName: 'RegisterOtp' },
);

export const AuthenticationPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "AuthenticationPage" */ 'app/pages/AppPage/LoginPage/Authentication'
    ),
  module => module.default,
  { fallback, chunkName: 'AuthenticationPage' },
);

export const RecoveryPage = lazyLoad(
  () => import(/* webpackChunkName: "RecoveryPage" */ 'app/pages/AppPage/LoginPage/Recovery'),
  module => module.default,
  { fallback, chunkName: 'RecoveryPage' },
);

export const DonationPage = lazyLoad(
  () => import(/* webpackChunkName: "DonationPage" */ 'app/pages/AppPage/DonationPage'),
  module => module.default,
  { fallback, chunkName: 'DonationPage' },
);

export const DonationBulkUploadPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "DonationBulkUploadPage" */ 'app/pages/AppPage/DonationPage/BulkUpload'
    ),
  module => module.default,
  { fallback, chunkName: 'DonationBulkUploadPage' },
);

export const BasicSettingsPage = lazyLoad(
  () => import(/* webpackChunkName: "BasicSettingsPage" */ 'app/pages/AppPage/BasicSettingsPage'),
  module => module.default,
  { fallback, chunkName: 'BasicSettingsPage' },
);

export const CorporationSettingPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "CorporationSettingPage" */ 'app/pages/AppPage/CorporationSettingPage'
    ),
  module => module.default,
  { fallback, chunkName: 'CorporationSettingPage' },
);

export const IndividualSettingsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IndividualSettingsPage" */ 'app/pages/AppPage/IndividualSettingsPage'
    ),
  module => module.default,
  { fallback, chunkName: 'IndividualSettingsPage' },
);

export const IndividualPage = lazyLoad(
  () => import(/* webpackChunkName: "IndividualPage" */ 'app/pages/AppPage/IndividualPage'),
  module => module.default,
  { fallback, chunkName: 'IndividualPage' },
);

export const IndividualBulkUpload = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IndividualBulkUpload" */ 'app/pages/AppPage/IndividualPage/BulkUpload'
    ),
  module => module.default,
  { fallback, chunkName: 'IndividualBulkUpload' },
);

export const IndividualNamingPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IndividualNamingPage" */ 'app/pages/AppPage/IndividualPage/Naming'
    ),
  module => module.default,
  { fallback, chunkName: 'IndividualNamingPage' },
);

export const NamingDetailPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "NamingDetailPage" */ 'app/pages/AppPage/IndividualPage/NamingDetail'
    ),
  module => module.default,
  { fallback, chunkName: 'NamingDetailPage' },
);

export const CorporationPage = lazyLoad(
  () => import(/* webpackChunkName: "CorporationPage" */ 'app/pages/AppPage/CorporationPage'),
  module => module.default,
  { fallback, chunkName: 'CorporationPage' },
);

export const CorporationBulkUpload = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "CorporationBulkUpload" */ 'app/pages/AppPage/CorporationPage/BulkUpload'
    ),
  module => module.default,
  { fallback, chunkName: 'CorporationBulkUpload' },
);

export const CorporationNamingPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "CorporationNamingPage" */ 'app/pages/AppPage/CorporationPage/Naming'
    ),
  module => module.default,
  { fallback, chunkName: 'CorporationNamingPage' },
);

export const ProjectPage = lazyLoad(
  () => import(/* webpackChunkName: "ProjectPage" */ 'app/pages/AppPage/ProjectPage'),
  module => module.default,
  { fallback, chunkName: 'ProjectPage' },
);

export const ProjectDetailPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ProjectDetailPage" */ 'app/pages/AppPage/ProjectPage/ProjectDetailPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ProjectDetailPage' },
);

export const ProjectEditPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ProjectEditPage" */ 'app/pages/AppPage/ProjectPage/ProjectEditPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ProjectEditPage' },
);

export const ReceiptPage = lazyLoad(
  () => import(/* webpackChunkName: "ReceiptPage" */ 'app/pages/AppPage/ReceiptPage'),
  module => module.default,
  { fallback, chunkName: 'ReceiptPage' },
);

export const CreateReceipt = lazyLoad(
  () =>
    import(/* webpackChunkName: "CreateReceipt" */ 'app/pages/AppPage/ReceiptPage/CreateReceipt'),
  module => module.default,
  { fallback, chunkName: 'CreateReceipt' },
);

export const ReCreateReceipt = lazyLoad(
  () =>
    import(/* webpackChunkName: "CreateReceipt" */ 'app/pages/AppPage/ReceiptPage/ReCreateReceipt'),
  module => module.default,
  { fallback, chunkName: 'ReCreateReceipt' },
);

export const CreateReceiptHistory = lazyLoad(
  () =>
    import(/* webpackChunkName: "CreateReceiptHistory" */ 'app/pages/AppPage/ReceiptPage/History'),
  module => module.default,
  { fallback, chunkName: 'CreateReceiptHistory' },
);

export const ReceiptHistoryDetail = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ReceiptHistoryDetail" */ 'app/pages/AppPage/ReceiptPage/HistoryDetail'
    ),
  module => module.default,
  { fallback, chunkName: 'ReceiptHistoryDetail' },
);

export const ReceiptBulkPage = lazyLoad(
  () => import(/* webpackChunkName: "ReceiptBulkPage" */ 'app/pages/AppPage/ReceiptBulkPage'),
  module => module.default,
  { fallback, chunkName: 'ReceiptBulkPage' },
);

export const ReccuringPage = lazyLoad(
  () => import(/* webpackChunkName: "ReccuringPage" */ 'app/pages/AppPage/ReccuringPage'),
  module => module.default,
  { fallback, chunkName: 'ReccuringPage' },
);

export const PaymentManagePage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "PaymentManagePage" */ 'app/pages/AppPage/PaymentPage/PaymentManagePage'
    ),
  module => module.default,
  { fallback, chunkName: 'PaymentManagePage' },
);

export const LearningPage = lazyLoad(
  () => import(/* webpackChunkName: "LearningPage" */ 'app/pages/AppPage/LearningPage'),
  module => module.default,
  { fallback, chunkName: 'LearningPage' },
);

export const DiscountPage = lazyLoad(
  () => import(/* webpackChunkName: "DiscountPage" */ 'app/pages/AppPage/DiscountPage'),
  module => module.default,
  { fallback, chunkName: 'DiscountPage' },
);

export const ContractPage = lazyLoad(
  () => import(/* webpackChunkName: "ContractPage" */ 'app/pages/AppPage/ContractPage'),
  module => module.default,
  { fallback, chunkName: 'ContractPage' },
);

export const ContractPaymentPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ContractPaymentPage" */ 'app/pages/AppPage/ContractPage/PaymentPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ContractPaymentPage' },
);

export const ContractBankThanksPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ContractBankThanksPage" */ 'app/pages/AppPage/ContractPage/BankThanksPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ContractBankThanksPage' },
);

export const ContractCreditThanksPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ContractCreditThanksPage" */ 'app/pages/AppPage/ContractPage/CreditThanksPage'
    ),
  module => module.default,
  { fallback, chunkName: 'ContractCreditThanksPage' },
);

export const NewsPage = lazyLoad(
  () => import(/* webpackChunkName: "NewsPage" */ 'app/pages/AppPage/NewsPage'),
  module => module.default,
  { fallback, chunkName: 'NewsPage' },
);

export const RedirectToLogin = lazyLoad(
  () => import(/* webpackChunkName: "RedirectToLogin" */ 'app/pages/RedirectToLogin'),
  module => module.default,
  { fallback, chunkName: 'RedirectToLogin' },
);

export const RedirectToOldCongrant = lazyLoad(
  () => import(/* webpackChunkName: "RedirectToOldCongrant" */ 'app/pages/RedirectToOldCongrant'),
  module => module.default,
  { fallback, chunkName: 'RedirectToOldCongrant' },
);

export const RedirectToOldCongrantReceipt = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "RedirectToOldCongrantReceipt" */ 'app/pages/RedirectToOldCongrantReceipt'
    ),
  module => module.default,
  { fallback, chunkName: 'RedirectToOldCongrantReceipt' },
);

export const RedirectToPaymentForm = lazyLoad(
  () => import(/* webpackChunkName: "RedirectToPaymentForm" */ 'app/pages/RedirectToPaymentForm'),
  module => module.default,
  { fallback, chunkName: 'RedirectToPaymentForm' },
);

export const RedirectToProjectComments = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "RedirectToProjectComments" */ 'app/pages/RedirectToProjectComments'
    ),
  module => module.default,
  { fallback, chunkName: 'RedirectToProjectComments' },
);

export const RedirectToProjectReports = lazyLoad(
  () =>
    import(/* webpackChunkName: "RedirectToProjectReports" */ 'app/pages/RedirectToProjectReports'),
  module => module.default,
  { fallback, chunkName: 'RedirectToProjectReports' },
);

export const MailLogsPage = lazyLoad(
  () => import(/* webpackChunkName: "MailLogsPage" */ 'app/pages/AppPage/MailLogsPage'),
  module => module.default,
  { fallback, chunkName: 'MailLogsPage' },
);

export const DownloadPage = lazyLoad(
  () => import(/* webpackChunkName: "DownloadPage" */ 'app/pages/AppPage/DownloadPage'),
  module => module.default,
  { fallback, chunkName: 'DownloadPage' },
);

export const PostalPaymentSuccessPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "PostalPaymentSuccessPage" */ 'app/pages/ClientPage/PostalPaymentSuccessPage'
    ),
  module => module.default,
  { fallback, chunkName: 'PostalPaymentSuccessPage' },
);

export const SupportRoutesPage = lazyLoad(
  () => import(/* webpackChunkName: "DonationPage" */ 'app/pages/AppPage/SupportRoutesPage'),
  module => module.default,
  { fallback, chunkName: 'SupportRoutesPage' },
);
